<script setup lang="ts">
import { ref } from "vue";
import type { DrawerProps } from "./drawer.types";
import {
  drawerOverlayClass,
  drawerHeaderClass,
  drawerLeadingClass,
  drawerBodyClass,
  drawerTrailingClass,
  drawerFooterClass,
} from "./drawer.styles";
import { useDrawer } from "./useDrawer";
import { useSlotPresence } from "composables/useSlotPresence";

const props = withDefaults(defineProps<DrawerProps>(), {
  side: "right",
  open: false,
  closeOnClickOutside: true,
  closeOnEscape: true,
  animated: true,
  overlay: true,
  resizable: false,
  defaultSize: 25,
  minSize: 10,
  maxSize: 80,
});

const contentRef = ref<HTMLElement | null>(null);
const overlayRef = ref<HTMLElement | null>(null);
const {
  classes,
  visible,
  overlayVisible,
  close,
  isResizable,
  drawerSizeStyle,
  resizableHandleClass,
  drawerHandleDirection,
  onHandlePointerDown,
} = useDrawer({ props, contentRef, overlayRef });

const {
  leading: hasLeading,
  trailing: hasTrailing,
  footer: hasFooter,
} = useSlotPresence(["leading", "trailing", "footer"]);
</script>

<template>
  <div v-show="overlayVisible" ref="overlayRef" :class="drawerOverlayClass" class="e-drawer-overlay" />

  <e-primitive
    v-show="visible"
    ref="contentRef"
    as="aside"
    :inner-class="classes"
    :style="drawerSizeStyle"
    class="e-drawer"
  >
    <!-- Resize handle strip (resizable mode only) — absolutely positioned at the inner edge -->
    <div v-if="isResizable" part="resize-handle" :class="resizableHandleClass" @pointerdown="onHandlePointerDown">
      <e-splitter-handle :direction="drawerHandleDirection" class="h-full w-full" />
    </div>

    <header part="header" class="e-drawer__header" :class="drawerHeaderClass">
      <div class="min-w-0">
        <slot name="header" />
      </div>
      <div>
        <e-button size="xs" variant="ghost" square icon="close" aria-label="Close" @click="close" />
      </div>
    </header>

    <div v-show="hasLeading" part="leading" class="e-drawer__leading" :class="drawerLeadingClass">
      <slot name="leading" />
    </div>

    <div part="body" class="e-drawer__body" :class="drawerBodyClass">
      <slot />
    </div>

    <div v-show="hasTrailing" part="trailing" class="e-drawer__trailing" :class="drawerTrailingClass">
      <slot name="trailing" />
    </div>

    <div v-show="hasFooter" part="footer" class="e-drawer__footer" :class="drawerFooterClass">
      <slot name="footer" />
    </div>
  </e-primitive>
</template>

<style>
:host {
  display: contents;
}
</style>
